import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockResetIcon from '@mui/icons-material/LockReset'
import { problemMessage } from '../../api/baseApi'
import {
  useAddAdministratorMutation,
  useListAdministratorsQuery,
  useRemoveAdministratorMutation,
  useResetAdministratorMfaMutation,
} from '../../api/operator'
import type { Administrator } from '../../api/operatorApi'
import { Loading, QueryError } from '../../ui/QueryState'
import { shown } from '../../api/useIntent'

/**
 * Who may edit this tenant's content, managed by email.
 *
 * An email with no account is invited; the operator never handles the
 * account id a membership is actually keyed on. Removal is immediate —
 * the next request that person makes is refused — and deletes nothing
 * but their standing here.
 *
 * Each administrator's own name, where they have set one, sits beside
 * the email, and whether they sign in with two-step verification beside
 * that. Resetting it is the recovery for a lost phone, so it is offered
 * whatever the listing says: the operator acts on what the person on the
 * phone tells them, and a reset of a factor that is already off changes
 * nothing.
 */
export function TenantAdministrators({ tenant, active }: { tenant: string; active: boolean }) {
  const administrators = useListAdministratorsQuery({ tenant })
  const [add, adding] = useAddAdministratorMutation()
  const [remove, removing] = useRemoveAdministratorMutation()
  const [resetting, setResetting] = useState<Administrator | null>(null)
  const [email, setEmail] = useState('')

  if (administrators.isLoading) return <Loading label="Loading administrators…" />
  if (administrators.error) return <QueryError error={administrators.error} title="Could not load administrators" />
  const held = administrators.data?.administrators ?? []

  return (
    <Stack spacing={2}>
      {Boolean(adding.error || removing.error) && (
        <Alert severity="error">{problemMessage(adding.error ?? removing.error, 'The change was not made.')}</Alert>
      )}
      <Paper>
        {held.length === 0 ? (
          <Typography sx={{ p: 2 }} color="text.secondary">
            Nobody administers this tenant.
          </Typography>
        ) : (
          <List disablePadding>
            {held.map((administrator) => (
              <ListItem
                key={administrator.subject}
                divider
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    {administrator.email !== undefined && (
                      <Tooltip title="Reset two-step verification">
                        <IconButton
                          aria-label={`Reset two-step verification for ${administrator.email}`}
                          onClick={() => setResetting(administrator)}
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove">
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${administrator.email ?? administrator.subject}`}
                        disabled={removing.isLoading}
                        onClick={() => remove({ tenant, subject: administrator.subject }).catch(shown)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={<AdministratorLine administrator={administrator} />}
                  secondary={administrator.subject}
                  sx={{ pr: 10 }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      {resetting && <ResetTwoStep tenant={tenant} administrator={resetting} onClose={() => setResetting(null)} />}
      {active && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="Email"
            size="small"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            helperText="They are invited by email if they have no account."
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="contained"
            disabled={!email.trim() || adding.isLoading}
            onClick={() =>
              add({ tenant, addAdministrator: { email: email.trim() } })
                .unwrap()
                .then(() => setEmail(''))
                .catch(shown)
            }
          >
            Add administrator
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

/**
 * The email, the name its holder gave beside it, and the two-step state
 * beside that. An account that no longer exists has neither.
 */
function AdministratorLine({ administrator }: { administrator: Administrator }) {
  if (administrator.email === undefined) return <>Account no longer exists</>
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap component="span">
      <span>{administrator.email}</span>
      {administrator.name && (
        <Typography component="span" color="text.secondary">
          {administrator.name}
        </Typography>
      )}
      <Chip
        size="small"
        variant="outlined"
        color={administrator.mfa ? 'success' : 'default'}
        label={administrator.mfa ? 'Two-step on' : 'Two-step off'}
      />
    </Stack>
  )
}

/**
 * Turning off someone's second factor, behind a confirmation.
 *
 * Whoever asks for this is usually on the phone saying they lost theirs,
 * and the reset lets anyone with the password in. So the dialog asks the
 * operator to be sure who they are talking to before it does anything.
 */
function ResetTwoStep({ tenant, administrator, onClose }: { tenant: string; administrator: Administrator; onClose: () => void }) {
  const [reset, resetting] = useResetAdministratorMfaMutation()
  const who = administrator.name ? `${administrator.name} (${administrator.email})` : administrator.email

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset two-step verification?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            <strong>{who}</strong> will sign in with their password alone, and can turn two-step verification on again
            from their account. It is turned off for every tenant they administer, not only this one.
          </DialogContentText>
          <Alert severity="warning">
            Make sure you are talking to the account holder. Anyone who knows their password can sign in once this is done.
          </Alert>
          {Boolean(resetting.error) && (
            <Alert severity="error">{problemMessage(resetting.error, 'Two-step verification was not reset.')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="warning"
          variant="contained"
          disabled={resetting.isLoading}
          onClick={() =>
            reset({ tenant, subject: administrator.subject })
              .unwrap()
              .then(onClose)
              .catch(shown)
          }
        >
          Reset two-step verification
        </Button>
      </DialogActions>
    </Dialog>
  )
}
